import { alpha } from '@mui/material/styles';

export default theme => ({
    root: {
        // Exposes the mobile top bar's height to descendant viewport calcs.
        // Zero on desktop (no top bar); the bar's height below lg.
        '--app-topbar-height': '0px',
        [theme.breakpoints.down('lg')]: {
            '--app-topbar-height': theme.height.topBar,
        },
    },
    blackout: {
        zIndex: theme.zIndex.blackout,
    },
    footer: {
        flex: '0 0 auto',
        width: '100%',
        transition: theme.transitions.create(['margin', 'width'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
        }),
        zIndex: 0,
        position: 'relative',
    },
    drawerRoot: {
        zIndex: theme.zIndex.modal,
    },
    drawerPaper: {
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        width: theme.width.drawer,
        backgroundColor: theme.palette.tertiary.main,
        color: theme.palette.common.white,
        overflowX: 'hidden',
        // The persistent drawer sets `transition: transform` inline on the paper,
        // which blocks the width animation — !important is needed to beat it so the
        // rail slides open/closed instead of snapping.
        transition: `${theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
        })} !important`,
        // Collapsed rail: icon width only.
        '&.collapsed': {
            width: theme.width.drawerMini,
        },
        // Peek: expanded on top of content (content is not shifted), so it needs
        // its own elevation to read over the page.
        '&.peeking': {
            boxShadow: `${theme.shadows[8]} !important`,
        },
    },
    sidebar: {
        width: theme.width.drawer,
        '&.collapsed': {
            width: theme.width.drawerMini,
        },
    },
    drawerScroller: {
        flex: '1 1 auto',
        minHeight: 0,
    },
    scrollRegion: {
        position: 'relative',
        flex: '1 1 auto',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
    },
    scrollShadowTop: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: 28,
        zIndex: 1,
        pointerEvents: 'none',
        opacity: 0,
        transition: theme.transitions.create('opacity'),
        background: `linear-gradient(to bottom, ${theme.palette.tertiary.main}, ${alpha(
            theme.palette.tertiary.main,
            0
        )})`,
    },
    scrollShadowBottom: {
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        height: 28,
        zIndex: 1,
        pointerEvents: 'none',
        opacity: 0,
        transition: theme.transitions.create('opacity'),
        background: `linear-gradient(to top, ${theme.palette.tertiary.main}, ${alpha(
            theme.palette.tertiary.main,
            0
        )})`,
    },
    scrollShadowVisible: {
        opacity: 1,
    },
    sectionDivider: {
        margin: theme.spacing(1, 2),
        borderColor: 'rgba(255, 255, 255, 0.12)',
    },
    content: {
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        paddingTop: 0,
        zIndex: 0,
        position: 'relative',
        backgroundColor: theme.palette.background.default,
        transition: theme.transitions.create('margin', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
        }),
        // Clear the fixed mobile top bar; no bar on desktop.
        [theme.breakpoints.down('lg')]: {
            paddingTop: theme.height.topBar,
        },
        '&:has(.min-page-width-md-breakpoint)': {
            overflow: 'auto',
        },
    },
    panel: {
        flex: '1 1 auto',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'stretch',
        position: 'relative',
        '&:has(.min-page-width-md-breakpoint)': {
            minWidth: theme.breakpoints.values.md,
        },
    },
    contentShift: {
        marginLeft: theme.width.drawer,
    },
    contentShiftMini: {
        marginLeft: theme.width.drawerMini,
    },
    logoWrapper: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        height: '64px',
        paddingTop: theme.spacing(1),
        flexShrink: 0,
    },
    logoLink: {
        fontSize: 0,
        flex: '0 0 auto',
        margin: theme.spacing(0, 2),
    },
    logo: {
        cursor: 'pointer',
        height: 'auto',
        width: '110px',
        // Bump the wordmark right so its O lands where the collapsed square mark
        // sits, so the O appears to stay put as the rail expands.
        marginLeft: theme.spacing(0.75),
        '& .logo .filled': {
            fill: theme.palette.common.white,
        },
    },
    logoSquare: {
        cursor: 'pointer',
        height: 'auto',
        // Matches the O in the expanded wordmark exactly (21px), so the mark
        // appears the same size in both states.
        width: '21px',
        // Nudge the single-org mark onto the nav icons' centerline in the rail.
        marginLeft: theme.spacing(0.75),
        '& .logo': {
            fill: theme.palette.common.white,
        },
    },
    logoDivider: {
        marginBottom: theme.spacing(1),
    },
    drawerToggle: {
        color: theme.palette.common.white,
    },
});
